import { readEnv } from "@repo/helpers";
import { type FC, type ReactNode, useState } from "react";
import { BodyTextM } from "../Typography";
import { formatDate } from "../../helpers";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { type TranslationKeys, useTypedTranslation } from "../../hooks/useTypedTranslation";
import * as Styled from "./ProfileInfoV2.styled";
import { ProfileInfoV2Section } from "./ProfileInfoV2Section";

export type ProfileInfoV2Data = {
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: ReactNode;
    birthNumber?: string | null;
    mobile?: ReactNode;
    email?: ReactNode;
};

export type ProfileInfoV2Props = {
    data?: ProfileInfoV2Data;
    isLoading?: boolean;
    onPersonalDataChange?: () => void;
    contactChangeAction?: ReactNode;
};

export const ProfileInfoV2: FC<ProfileInfoV2Props> = ({
    data,
    isLoading = false,
    onPersonalDataChange,
    contactChangeAction,
}) => {
    const { t } = useTypedTranslation();
    const isDesktop = useIsDesktop();
    const [isPersonalInfoOpen, setIsPersonalInfoOpen] = useState(false);
    const shouldUseAuthorizationContacts = readEnv("VITE_USE_AUTHORIZATION_CONTACTS") === "true";

    const personalInformation = [
        {
            label: t("global.name"),
            value: data?.firstName,
        },
        {
            label: t("global.surname"),
            value: data?.lastName,
        },
        {
            label: t("global.birthDate"),
            value: data?.dateOfBirth,
        },
        {
            label: t("global.birthNumber"),
            value: data?.birthNumber,
        },
    ];

    const contactInformation = [
        {
            label: t(shouldUseAuthorizationContacts ? "global.authorizationPhone" : "global.phone"),
            value: data?.mobile,
        },
        {
            label: t(shouldUseAuthorizationContacts ? "global.authorizationEmail" : "global.email"),
            value: data?.email,
        },
    ];

    return (
        <Styled.Root>
            <ProfileInfoV2Section
                title={t("profile.menu.myData")}
                fields={personalInformation}
                isLoading={isLoading}
                action={
                    shouldUseAuthorizationContacts ? (
                        <Styled.PersonalChangeButton
                            variant="text"
                            size="small"
                            onClick={() => {
                                onPersonalDataChange?.();
                                setIsPersonalInfoOpen(true);
                            }}
                        >
                            {t("global.change")}
                        </Styled.PersonalChangeButton>
                    ) : null
                }
            />
            <ProfileInfoV2Section
                title={t("profile.personalInfo.contactInfo")}
                fields={contactInformation}
                isLoading={isLoading}
                action={contactChangeAction}
            />
            <Styled.ChangeDialog
                open={isPersonalInfoOpen}
                onClose={() => setIsPersonalInfoOpen(false)}
                fullScreen={!isDesktop}
            >
                <Styled.ChangeDialogContent>
                    <BodyTextM>{t("profile.personalInfo.changePortalDataInfo")}</BodyTextM>
                    <Styled.ChangeDialogCloseButton onClick={() => setIsPersonalInfoOpen(false)}>
                        {t("profile.personalInfo.iUnderstand")}
                    </Styled.ChangeDialogCloseButton>
                </Styled.ChangeDialogContent>
            </Styled.ChangeDialog>
        </Styled.Root>
    );
};

export type ProfileV2Data = {
    client?: {
        firstName?: string | null;
        lastName?: string | null;
        dateOfBirth?: string | Date | null;
        birthNumber?: string | null;
    } | null;
    mobile?: string | null;
    email?: string | null;
};

type ProfileV2ContactRenderers = {
    renderMobile?: (mobile: string) => ReactNode;
    renderEmail?: (email: string) => ReactNode;
};

const createContactInformation = (
    data: ProfileV2Data | undefined,
    { renderMobile, renderEmail }: ProfileV2ContactRenderers,
    t: (key: TranslationKeys, options?: Record<string, unknown>) => string,
) => [
    {
        label: t("global.authorizationPhone"),
        value: data?.mobile ? (renderMobile?.(data.mobile) ?? data.mobile) : undefined,
    },
    {
        label: t("global.authorizationEmail"),
        value: data?.email ? (renderEmail?.(data.email) ?? data.email) : undefined,
    },
];

export type ProfileV2Props = ProfileV2ContactRenderers & {
    data?: ProfileV2Data;
    isLoading?: boolean;
    onPersonalDataChange?: () => void;
    contactChangeAction?: ReactNode;
};

export const ProfileV2: FC<ProfileV2Props> = ({
    data,
    isLoading = false,
    onPersonalDataChange,
    contactChangeAction,
    renderMobile,
    renderEmail,
}) => {
    const profileData: ProfileInfoV2Data = {
        firstName: data?.client?.firstName,
        lastName: data?.client?.lastName,
        dateOfBirth: data?.client?.dateOfBirth ? formatDate(data.client.dateOfBirth) : undefined,
        birthNumber: data?.client?.birthNumber,
        mobile: data?.mobile ? (renderMobile?.(data.mobile) ?? data.mobile) : undefined,
        email: data?.email ? (renderEmail?.(data.email) ?? data.email) : undefined,
    };

    return (
        <ProfileInfoV2
            data={profileData}
            isLoading={isLoading}
            onPersonalDataChange={onPersonalDataChange}
            contactChangeAction={contactChangeAction}
        />
    );
};

export type ProfileContactV2Props = ProfileV2ContactRenderers & {
    data?: ProfileV2Data;
    isLoading?: boolean;
    action?: ReactNode;
};

export const ProfileContactV2: FC<ProfileContactV2Props> = ({
    data,
    isLoading = false,
    action,
    renderMobile,
    renderEmail,
}) => {
    const { t } = useTypedTranslation();

    return (
        <ProfileInfoV2Section
            title={t("profile.personalInfo.contactInfo")}
            fields={createContactInformation(data, { renderMobile, renderEmail }, t)}
            isLoading={isLoading}
            action={action}
        />
    );
};
